import React from "react";

import { EventQuery, mapQueryToString } from "lib/build/events";

import { fetchWithRetries } from "src/common/utils/api";
import { useCreateGenericError } from "src/features/notifications";
import { useOrgModParams } from "src/features/orgmod";
import { RawPatient } from "src/modules/shared/patient-list/types";

type PatientListAction<T> =
	| FetchStartAction
	| FetchSuccessAction<T>
	| FetchFailureAction;

interface FetchStartAction {
	type: "FETCH_START";
}

interface FetchSuccessAction<T> {
	type: "FETCH_SUCCEESS";
	payload: { patients: T[] };
}

interface FetchFailureAction {
	type: "FETCH_FAILURE";
	payload: { error: unknown };
}

interface PatientListState<T> {
	isLoading: boolean;
	error: unknown | null;
	patients: T[];
}

const initialState = {
	isLoading: false,
	error: null,
	patients: []
};

export function usePatientList<T>(
	queries: EventQuery[],
	normalize: (patient: RawPatient) => T
): PatientListState<T> {
	const [state, dispatch] = React.useReducer<
		PatientListState<T>,
		[PatientListAction<T>]
	>(reducer, initialState);

	const createGenericError = useCreateGenericError();

	const { org, mod } = useOrgModParams();

	const params: string[] = [];
	params.push(`organization=${org}`);
	params.push(`module=${mod}`);
	for (const query of queries) {
		params.push(`i=${mapQueryToString(query)}`);
	}
	const url = `/patients?${params.join("&")}`;

	const fetchPatients = React.useCallback(async () => {
		dispatch({ type: "FETCH_START" });
		try {
			const { data } = await fetchWithRetries<RawPatient[]>(url, 0);
			dispatch({
				type: "FETCH_SUCCEESS",
				payload: { patients: data.map(normalize) }
			});
		} catch (error) {
			dispatch({ type: "FETCH_FAILURE", payload: { error } });
			createGenericError();
		}
	}, [normalize, createGenericError, url]);

	React.useEffect(() => {
		fetchPatients();
	}, [fetchPatients]);

	return state as PatientListState<T>;
}

function reducer<T>(
	state: PatientListState<T>,
	action: PatientListAction<T>
): PatientListState<T> {
	if (action.type === "FETCH_START") {
		return handleFetchStart(state);
	}
	if (action.type === "FETCH_SUCCEESS") {
		return handleFetchSuccess(state, action);
	}
	if (action.type === "FETCH_FAILURE") {
		return handleFetchFailure(state, action);
	}
	return state;
}

function handleFetchStart<T>(state: PatientListState<T>): PatientListState<T> {
	return {
		...state,
		isLoading: true,
		error: null,
		patients: []
	};
}

function handleFetchSuccess<T>(
	state: PatientListState<T>,
	{ payload: { patients } }: FetchSuccessAction<T>
): PatientListState<T> {
	return {
		...state,
		isLoading: false,
		patients
	};
}

function handleFetchFailure<T>(
	state: PatientListState<T>,
	{ payload: { error } }: FetchFailureAction
): PatientListState<T> {
	return {
		...state,
		isLoading: false,
		error
	};
}
